import { useEffect, useMemo, useState } from 'react';
import { useIsFetching, useQuery } from '@tanstack/react-query';

import { AQUATIC_RESOURCES_QUERY_KEYS, AquaticResourcesQueries } from './query';
import { GetAquaticResourcesQueryParams, GetValidateAquaticResourcesQueryParams } from './types';

const useValidationLoader = (isFetching: boolean) => {
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (!isFetching) {
      setShowLoader(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setShowLoader(true);
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [isFetching]);

  return showLoader;
};

export const useIsAquaticResourceValidationFetching = (requestId?: string) => {
  const fetchCount = useIsFetching({
    queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_VALIDATIONS, requestId],
  });

  return fetchCount > 0;
};

export const useGetAquaticResources = (params: GetAquaticResourcesQueryParams, requestId: string) => {
  const getArsQuery = useQuery({
    queryKey: [
      AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES,
      requestId,
      params.page,
      params.pageSize,
      params.column,
      params.dir,
    ],
    queryFn: () => AquaticResourcesQueries.getAquaticResources(params),
  });

  const getArsResponse = getArsQuery.data?.data;

  return {
    isLoading: getArsQuery.isFetching,
    items: getArsResponse?.items,
    totalCount: getArsResponse?.totalCount ?? 0,
  };
};

export const useGetAquaticResourceGeometries = (requestId: string) => {
  const getArGeometries = useQuery({
    queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_GEOMETRIES, requestId],
    queryFn: () => AquaticResourcesQueries.getAquaticResourceGeometries(),
  });

  return getArGeometries.data?.data;
};

export const useGetAquaticResourceValidations = (
  params: GetValidateAquaticResourcesQueryParams,
  requestId: string,
  isDraft?: boolean
) => {
  const validationQuery = useQuery({
    queryKey: [
      AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_VALIDATIONS,
      requestId,
      params.pageSize,
      params.column,
      params.dir,
    ],
    queryFn: () => AquaticResourcesQueries.validateAquaticResources(params),
    enabled: isDraft,
  });

  const showValidationLoader = useValidationLoader(validationQuery.isFetching);
  const validationData = validationQuery.data?.data;
  const jdTypes = useMemo(() => validationData?.jdTypes ?? [], [validationData?.jdTypes]);

  const invalidPages = useMemo(() => validationData?.invalidPages ?? [], [validationData?.invalidPages]);

  const aquaticResourceValidations = useMemo(
    () => validationData?.aquaticResourceValidations ?? {},
    [validationData?.aquaticResourceValidations]
  );

  const hasJdTypeMismatch = useMemo(
    () => Object.values(aquaticResourceValidations).some((validation) => validation.hasJdTypeMismatch === true),
    [aquaticResourceValidations]
  );

  return {
    invalidPages,
    aquaticResourceValidations,
    jdTypes,
    hasJdTypeMismatch,
    validationUpdatedAt: validationQuery.dataUpdatedAt,
    isValidationFetching: validationQuery.isFetching,
    showValidationLoader,
    refetch: validationQuery.refetch,
  };
};

export const useGetAquaticResourceUsaceDistrictValidation = (requestId?: string, version?: number) =>
  useQuery({
    queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_USACE_DISTRICT_VALIDATION, requestId, version],
    queryFn: () => AquaticResourcesQueries.validateAquaticResourceUsaceDistrict(),
    enabled: requestId != null && version != null,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useGetAquaticResourcesWaterNames = (enabled = true) =>
  useQuery({
    queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_WATERNAMES],
    queryFn: () => AquaticResourcesQueries.getAquaticResourcesWaterNames(),
    enabled,
    staleTime: 1000 * 60 * 60,
  });
